import { motion, AnimatePresence } from 'framer-motion';

interface NetworkModalProps {
  isOpen: boolean;
  networkName: string;
  currentChainId?: number;
  expectedChainId: number;
  onSwitchNetwork: () => void;
  canSwitchNetwork: boolean;
}

export const NetworkModal = ({
  isOpen,
  networkName,
  currentChainId,
  expectedChainId,
  onSwitchNetwork,
  canSwitchNetwork,
}: NetworkModalProps) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          {/* Modal Container */}
          <div className="fixed left-0 right-0 top-1/2 -translate-y-1/2 z-[101] max-w-[var(--app-max-width)] mx-auto flex justify-center items-center pointer-events-none">
            <motion.div
              className="relative w-[339px] flex flex-col items-center p-6 isolate border-2 border-[rgba(46,64,86,0.2)] rounded-2xl pointer-events-auto"
              style={{
                background:
                  'linear-gradient(to bottom, #1e3a8a 0%, #3b82f6 25%, #06b6d4 45%, #7c3aed 70%, #a78bfa 100%)',
              }}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
            >
              {/* Header */}
              <div className="mb-4 text-center">
                <h3
                  className="text-2xl font-bold text-white mb-2"
                  style={{
                    WebkitTextStroke: '1px #000',
                    textShadow: '1px 2px 0px #000',
                  }}
                >
                  Wrong Network
                </h3>
                <p
                  className="text-base text-white"
                  style={{
                    WebkitTextStroke: '0.5px #000',
                    textShadow: '0.5px 1px 0px #000',
                  }}
                >
                  You're connected to the wrong network. Please switch to{' '}
                  {networkName}.
                </p>
              </div>

              {/* Network Info */}
              <div className="mb-6 p-4 w-full bg-black/30 backdrop-blur-sm rounded-xl border border-white/20">
                <div className="flex justify-between items-center mb-2">
                  <span
                    className="text-white text-base font-bold"
                    style={{
                      WebkitTextStroke: '0.1px #000',
                      textShadow: '0.1px 0.5px 0px #000',
                    }}
                  >
                    Current:
                  </span>
                  <span
                    className="text-red-400 text-base font-bold"
                    style={{
                      WebkitTextStroke: '0.1px #000',
                      textShadow: '0.1px 0.5px 0px #000',
                    }}
                  >
                    Chain ID {currentChainId || 'Unknown'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span
                    className="text-white text-base font-bold"
                    style={{
                      WebkitTextStroke: '0.1px #000',
                      textShadow: '0.1px 0.5px 0px #000',
                    }}
                  >
                    Required:
                  </span>
                  <span
                    className="text-green-400 text-base font-bold"
                    style={{
                      WebkitTextStroke: '0.1px #000',
                      textShadow: '0.1px 0.5px 0px #000',
                    }}
                  >
                    {networkName} ({expectedChainId})
                  </span>
                </div>
              </div>

              {/* Action Button */}
              {canSwitchNetwork ? (
                <motion.button
                  onClick={onSwitchNetwork}
                  className="relative flex items-center justify-center w-full px-6 py-3 gap-3 isolate h-12 border-2 border-black rounded-xl overflow-hidden"
                  style={{
                    background:
                      'linear-gradient(135deg, #FF7A00 0%, #FFA500 49.04%, #FFD700 100%)',
                  }}
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ duration: 0.15 }}
                >
                  <div className="absolute inset-x-2 top-2 h-7 bg-white/30 rounded-lg z-0" />
                  <span className="font-lilita text-[24px] leading-8 text-white relative z-10 [text-shadow:1px_2px_0px_#000] [-webkit-text-stroke:1px_#000]">
                    Switch to {networkName}
                  </span>
                </motion.button>
              ) : (
                <div className="text-center w-full">
                  <p className="text-yellow-400 text-sm mb-3 font-bold">
                    Please manually switch to {networkName} in your wallet
                  </p>
                  <div className="text-xs text-gray-600">
                    Network switching not supported by your wallet
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};
